import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getCryptoMarkets, getStockMarkets, getCommodityMarkets, MARKET_CATEGORIES } from "../../config/markets";
import { rateLimitedFetch } from "../../utils/coinGeckoRateLimiter";
import { resolveMarketIcon } from "../../utils/marketIcons";
import { formatPriceWithSubscript } from "../utils/formatter";
import { ReactComponent as RocketIcon } from "../../assets/images/Perps/Rocket.svg";

// Reuse the CoinGecko image URL mapping from TradingTopBar
const imageMap = {
  bitcoin: { id: "1", filename: "bitcoin.png" },
  ethereum: { id: "279", filename: "ethereum.png" },
  cosmos: { id: "1481", filename: "cosmos_hub.png" },
  solana: { id: "4128", filename: "solana.png" },
  ripple: { id: "44", filename: "xrp-symbol-white-128.png" },
  litecoin: { id: "2", filename: "litecoin.png" },
  binancecoin: { id: "825", filename: "bnb-icon2_2x.png" },
  near: { id: "10365", filename: "near.jpg" },
  hyperliquid: { id: "50882", filename: "hyperliquid.jpg" },
  "official-trump": { id: "53746", filename: "trump.png" },
  sui: { id: "26375", filename: "sui-ocean-square.png" },
  ethena: { id: "36530", filename: "ethena.png" },
  arbitrum: { id: "16547", filename: "arb.jpg" },
  tron: { id: "1094", filename: "tron-logo.png" },
  aptos: { id: "26455", filename: "Aptos-Network-Symbol-Black-RGB-1x.png" },
  "polygon-ecosystem-token": { id: "32440", filename: "pol.png" },
  "the-open-network": { id: "17980", filename: "photo_2024-09-10_17.09.00.jpeg" },
  cardano: { id: "975", filename: "cardano.png" },
  dogecoin: { id: "5", filename: "dogecoin.png" },
  "aster-2": { id: "69040", filename: "_ASTER.png" },
  "pump-fun": { id: "67164", filename: "pump.jpg" },
  "avalanche-2": { id: "12559", filename: "Avalanche_Circle_RedWhite_Trans.png" },
  aave: { id: "12645", filename: "aave-token-round.png" },
  "pudgy-penguins": { id: "52622", filename: "PUDGY_PENGUINS_PENGU_PFP.png" },
  chainlink: { id: "877", filename: "Chainlink_Logo_500.png" },
  bonk: { id: "28600", filename: "bonk.jpg" },
  "shiba-inu": { id: "11939", filename: "shiba.png" },
  kaito: { id: "54411", filename: "Qm4DW488_400x400.jpg" },
  mantle: { id: "30980", filename: "Mantle-Logo-mark.png" },
  "story-2": { id: "54035", filename: "Transparent_bg.png" },
  "virtual-protocol": { id: "34057", filename: "LOGOMARK.png" },
  zcash: { id: "486", filename: "circle-zcash-color.png" },
  nibiru: { id: "34980", filename: "favicon-256x256.png" },
};

function getCoinImageUrl(coinGeckoId) {
  if (!coinGeckoId) return null;
  const info = imageMap[coinGeckoId];
  if (!info) return null;
  return `https://coin-images.coingecko.com/coins/images/${info.id}/large/${info.filename}`;
}

// Low-price token IDs that need special formatting (same as TradingTopBar)
const lowPriceTokenIds = new Set(["pump", "pengu", "bonk", "shib", "nibi"]);

// formatPriceWithSubscript imported from utils/formatter

// Module-level cache for gainers data
let gainersCache = { data: null, timestamp: 0 };
const CACHE_TTL = 2 * 60 * 1000; // 2 minutes

const TrendingMarquee = () => {
  const [gainers, setGainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const marqueeRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchGainers = async () => {
      // Check cache
      if (gainersCache.data && Date.now() - gainersCache.timestamp < CACHE_TTL) {
        setGainers(gainersCache.data);
        setLoading(false);
        return;
      }

      try {
        // ── Crypto from CoinGecko ──
        const cryptoMarkets = getCryptoMarkets();
        const coinIds = cryptoMarkets
          .map((m) => m.coinGeckoId)
          .filter(Boolean)
          .join(",");

        const cryptoPromise = (async () => {
          const response = await rateLimitedFetch(
            `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${coinIds}&order=market_cap_desc&sparkline=false&price_change_percentage=24h`
          );
          if (!response.ok) throw new Error("CoinGecko API error");
          const data = await response.json();

          const marketMap = {};
          cryptoMarkets.forEach((m) => {
            if (m.coinGeckoId) marketMap[m.coinGeckoId] = m;
          });

          return data.map((coin) => {
            const market = marketMap[coin.id];
            return {
              id: coin.id,
              marketId: market?.id || coin.id,
              symbol: market?.symbol || coin.symbol?.toUpperCase(),
              name: market?.name || coin.name,
              price: coin.current_price,
              change: coin.price_change_percentage_24h ?? 0,
              volume: coin.total_volume ?? 0,
              image: getCoinImageUrl(coin.id) || coin.image,
              category: 'crypto',
            };
          });
        })();

        // ── Stocks & Commodities from Nibiru candles API ──
        const equityPromise = (async () => {
          const equityMarkets = [...getStockMarkets(), ...getCommodityMarkets()];
          if (equityMarkets.length === 0) return [];

          const now = Math.floor(Date.now() / 1000);
          const fiveDaysAgo = now - 432000;
          const results = [];

          await Promise.all(
            equityMarkets.map(async (market) => {
              try {
                const res = await fetch(
                  `https://sai-candles.nibiru.fi/candles/udf/history?symbol=${market.symbol}&resolution=1D&from=${fiveDaysAgo}&to=${now}`
                );
                const data = await res.json();
                if (data && data.c && data.c.length > 0) {
                  const latestClose = data.c[data.c.length - 1];
                  const prevClose = data.c.length > 1 ? data.c[data.c.length - 2] : data.o[data.o.length - 1];
                  const change = prevClose && prevClose !== 0 ? ((latestClose - prevClose) / prevClose) * 100 : 0;
                  results.push({
                    id: market.id,
                    marketId: market.id,
                    symbol: market.symbol,
                    name: market.name,
                    price: latestClose,
                    change,
                    volume: data.v ? data.v[data.v.length - 1] : 0,
                    image: resolveMarketIcon(market.iconPath),
                    category: market.category,
                  });
                }
              } catch (_) { /* skip failed */ }
            })
          );
          return results;
        })();

        const [cryptoData, equityData] = await Promise.all([cryptoPromise, equityPromise]);

        const mapped = [...cryptoData, ...equityData]
          .sort((a, b) => (b.change ?? 0) - (a.change ?? 0));

        gainersCache = { data: mapped, timestamp: Date.now() };
        setGainers(mapped);
      } catch (err) {
        console.warn("GainersMarquee fetch error:", err.message);
        // If cache exists (even expired), use it
        if (gainersCache.data) {
          setGainers(gainersCache.data);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchGainers();
    const interval = setInterval(fetchGainers, CACHE_TTL);
    return () => clearInterval(interval);
  }, []);

  if (loading || gainers.length === 0) return null;

  // Duplicate items for seamless infinite scroll
  const items = [...gainers, ...gainers];

  // Scale animation duration with item count so scroll speed stays constant
  // Original: ~32 items at 80s ≈ 2.5s per item
  const duration = Math.max(40, gainers.length * 2.5);

  return (
    <>
      <style>{`
        @keyframes marquee-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .gainers-marquee-track {
          animation: marquee-scroll ${duration}s linear infinite;
        }
        .gainers-marquee-track:hover {
          animation-play-state: paused;
        }
      `}</style>

      {/* Docked to the bottom of the viewport as a footer rail: always visible
          while the page scrolls, and slim enough not to eat the screen. The
          trading pages subtract `--app-marquee-h` from their own height so
          nothing ends up underneath it. */}
      <div
        className="ce-marquee-rail w-full overflow-hidden fixed bottom-0 left-0 right-0"
        style={{
          height: "var(--app-marquee-h, 36px)",
          padding: "0 16px",
          background: "var(--surface)",
          // No top border. The rail is a surface floating over the page, and
          // the upward shadow is its edge — a hairline *and* a shadow is the
          // one combination the system never uses.
          border: 0,
          boxShadow: "0 -6px 24px rgba(0, 0, 0, .07)",
          zIndex: 120,
        }}
        ref={marqueeRef}
      >
        <div className="flex items-center h-full gap-[24px]">
          {/* Trending label */}
          {/* The label is a sunken pill: it is a caption for the rail beside
              it, not a control, so it sits *in* the surface rather than on
              top of it. */}
          <div
            className="flex items-center gap-1.5 flex-shrink-0"
            style={{
              padding: "3px 10px",
              borderRadius: 999,
              background: "var(--surface-2)",
              boxShadow: "var(--inset-sm)",
            }}
          >
            <RocketIcon className="w-[14px] h-[14px]" />
            <span
              className="whitespace-nowrap"
              style={{
                fontFamily: "General Sans",
                fontWeight: 600,
                fontSize: 11.5,
                lineHeight: "16px",
                color: "var(--ink-2)",
              }}
            >
              Trending
            </span>
          </div>

          {/* Marquee container with fade edges */}
          <div
            className="overflow-hidden flex-1 relative"
            style={{
              maskImage:
                "linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(to right, transparent 0%, black 5%, black 95%, transparent 100%)",
            }}
          >
            <div className="gainers-marquee-track flex items-center gap-[56px] w-max">
              {items.map((coin, idx) => {
                const isLow = lowPriceTokenIds.has(coin.marketId);
                const isEquity = coin.category === MARKET_CATEGORIES.STOCKS || coin.category === MARKET_CATEGORIES.COMMODITIES;
                const priceStr = isEquity && coin.price >= 1
                  ? Number(coin.price).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  : formatPriceWithSubscript(coin.price, isLow);

                const handleClick = () => {
                  if (coin.category === MARKET_CATEGORIES.STOCKS) navigate(`/perps/markets/stocks/${coin.marketId}`);
                  else if (coin.category === MARKET_CATEGORIES.COMMODITIES) navigate(`/perps/markets/commodities/${coin.marketId}`);
                  else navigate(`/perps/markets/crypto/${coin.marketId}`);
                };

                return (
                  <div
                    key={`${coin.id}-${idx}`}
                    className="flex items-center flex-shrink-0 gap-[8px] cursor-pointer"
                    style={{ height: 22 }}
                    onClick={handleClick}
                  >
                    {/* Logo */}
                    {coin.image && (
                      /* An 18px `--logo-plate` disc, for the same reason the
                         topbar's icon tile is one: a great many brand marks
                         are solid black on transparent and vanish on a dark
                         surface. The plate stays light in both themes. */
                      <span
                        className="flex items-center justify-center flex-shrink-0"
                        style={{
                          width: 18, height: 18, borderRadius: 999,
                          background: "var(--logo-plate)",
                          boxShadow: "inset 0 0 0 1px var(--logo-plate-line)",
                          overflow: "hidden",
                        }}
                      >
                        <img
                          src={coin.image}
                          alt={coin.symbol}
                          /* Fills the plate rather than floating 14px inside
                             it: most marks ship with their own square
                             background, and inset like that they read as a
                             square pasted on a disc with a ring around it.
                             Covering the plate makes every logo the same
                             clean circle; the plate still shows through the
                             transparent ones. */
                          className="w-full h-full object-cover rounded-full"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      </span>
                    )}
                    {/* Symbol */}
                    <span className="text-[11.5px] font-semibold whitespace-nowrap" style={{ color: "var(--ink)" }}>
                      {coin.symbol}
                    </span>
                    {/* Price */}
                    <span className="ce-figure text-[11.5px] whitespace-nowrap" style={{ color: "var(--ink-2)" }}>
                      ${priceStr}
                    </span>
                    {/* Change */}
                    <span
                      className="ce-figure text-[11.5px] font-semibold whitespace-nowrap"
                      style={{ color: coin.change >= 0 ? "var(--up)" : "var(--down)" }}
                    >
                      {coin.change >= 0 ? "+" : ""}{coin.change.toFixed(1)}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TrendingMarquee;
